/**
 * v3.0.0 の評価データマッパー
 *
 * データ構造 (functions/src/prompts/v3.0.0/evaluationService.ts の buildAnalyticsEvaluationRecord と一致):
 * - evaluationVersion: "3.0.0"
 * - topic: { id, theme, background, genre, difficulty }
 * - questionType / claimType はルート直下
 * - input: { userOpinion, understanding? }
 * - topicContent: ルート直下（任意）
 * - app_version / promptVersion
 * - llmModels: { readingScoring (課題文型以外は null), contentScoring, structureExpressionScoring, improvement }
 * - result: { questionType, claimType, category, axes[], improvement, totalScore }
 *   v2系の軸ごとのネスト構造ではなく、軸の配列（満点も軸ごとに保存）になっている
 *
 * バージョン分離ルール: 他バージョンのマッパーや型を import せず、v3.0.0 用に自前で持つ。
 */

import { DocumentData } from "firebase/firestore";
import {
  EvaluationListItem,
  EvaluationDetailV3_0_0,
  EvaluationAxisKindV3_0,
  EvaluationAxisResultV3_0,
  EvaluationLlmModelsV3_0_0,
  ImprovementV3_0,
  ImprovementPriorityV3_0,
  ScoredChecklistItemV3_0,
  QuestionType,
  ClaimType,
  RubricCategoryKind,
} from "../../types";
import { getTimestampSeconds } from "./utils";

const mapScoredChecklist = (rawChecklist: unknown): ScoredChecklistItemV3_0[] => {
  if (!Array.isArray(rawChecklist)) return [];
  return rawChecklist.map((rawItem) => ({
    item: rawItem?.item || "",
    reason: rawItem?.reason || "",
    point: typeof rawItem?.point === "number" ? rawItem.point : 0,
    maxPoint: typeof rawItem?.maxPoint === "number" ? rawItem.maxPoint : 0,
    evidence: typeof rawItem?.evidence === "string" ? rawItem.evidence : undefined,
  }));
};

const mapAxes = (rawAxes: unknown): EvaluationAxisResultV3_0[] => {
  if (!Array.isArray(rawAxes)) return [];
  return rawAxes.map((rawAxis) => ({
    axis: rawAxis?.axis as EvaluationAxisKindV3_0,
    label: rawAxis?.label || "",
    score: typeof rawAxis?.score === "number" ? rawAxis.score : 0,
    maxScore: typeof rawAxis?.maxScore === "number" ? rawAxis.maxScore : 0,
    explanation: rawAxis?.explanation || "",
    scoredChecklist: mapScoredChecklist(rawAxis?.scoredChecklist),
  }));
};

// 想定外の値は medium に寄せる
const mapImprovementPriority = (rawPriority: unknown): ImprovementPriorityV3_0 => {
  if (rawPriority === "high" || rawPriority === "low") return rawPriority;
  return "medium";
};

const mapImprovements = (rawImprovements: unknown): ImprovementV3_0[] => {
  if (!Array.isArray(rawImprovements)) return [];
  return rawImprovements.map((rawImprovement) => ({
    title: rawImprovement?.title || "",
    scope: rawImprovement?.scope === "structural" ? "structural" : "partial",
    priority: mapImprovementPriority(rawImprovement?.priority),
    targetQuote: rawImprovement?.targetQuote ?? null,
    explanation: rawImprovement?.explanation || "",
    rewriteExample: rawImprovement?.rewriteExample ?? null,
  }));
};

const mapLlmModels = (rawLlmModels: unknown): EvaluationLlmModelsV3_0_0 | undefined => {
  if (typeof rawLlmModels !== "object" || rawLlmModels === null) return undefined;
  const models = rawLlmModels as Record<string, unknown>;
  return {
    readingScoring: typeof models.readingScoring === "string" ? models.readingScoring : null,
    contentScoring: typeof models.contentScoring === "string" ? models.contentScoring : "",
    structureExpressionScoring:
      typeof models.structureExpressionScoring === "string"
        ? models.structureExpressionScoring
        : "",
    improvement: typeof models.improvement === "string" ? models.improvement : "",
  };
};

/**
 * v3.0.0データを一覧アイテムに変換
 */
export function mapV3_0_0ToListItem(docId: string, data: DocumentData): EvaluationListItem {
  const userOpinion = data.input?.userOpinion || "";
  const theme = data.topic?.theme || "";
  const topicContent = data.topicContent || theme;

  return {
    id: docId,
    version: "v3.0.0",
    timestamp: { _seconds: getTimestampSeconds(data.timestamp) },
    topic: {
      id: data.topic?.id || "",
      content: topicContent,
      genre: data.topic?.genre || "",
      difficulty: data.topic?.difficulty || 0,
      theme,
      questionType: (data.questionType as QuestionType) || undefined,
      claimType: (data.claimType as ClaimType) || undefined,
      background: data.topic?.background || undefined,
    },
    userOpinionPreview:
      userOpinion.length > 100 ? userOpinion.substring(0, 100) + "..." : userOpinion,
    totalScore: data.result?.totalScore || 0,
    appVersion: data.app_version || "",
  };
}

/**
 * v3.0.0データを詳細に変換
 */
export function mapV3_0_0ToDetail(docId: string, data: DocumentData): EvaluationDetailV3_0_0 {
  const resultData = data.result || {};
  const improvementBlock = resultData.improvement || {};

  const understanding =
    data.input?.understanding != null
      ? {
          claim: data.input.understanding.claim || "",
          grounds: data.input.understanding.grounds || "",
        }
      : undefined;

  return {
    id: docId,
    version: "v3.0.0",
    timestamp: { _seconds: getTimestampSeconds(data.timestamp) },
    topic: {
      id: data.topic?.id || "",
      theme: data.topic?.theme || "",
      background: data.topic?.background || undefined,
      genre: data.topic?.genre || "",
      difficulty: data.topic?.difficulty || 0,
      content: data.topicContent || undefined,
      questionType: (data.questionType as QuestionType) || undefined,
      claimType: (data.claimType as ClaimType | null) ?? undefined,
    },
    input: {
      userOpinion: data.input?.userOpinion || "",
      understanding,
    },
    result: {
      questionType: (resultData.questionType as QuestionType) || undefined,
      claimType: (resultData.claimType as ClaimType | null) ?? undefined,
      category: (resultData.category as RubricCategoryKind) || undefined,
      axes: mapAxes(resultData.axes),
      improvement: {
        shortReview: improvementBlock.shortReview || "",
        improvements: mapImprovements(improvementBlock.improvements),
        strengths: improvementBlock.strengths || [],
        improvedExample: improvementBlock.improvedExample || "",
      },
      totalScore: resultData.totalScore || 0,
    },
    appVersion: data.app_version || "",
    promptVersion: typeof data.promptVersion === "number" ? data.promptVersion : undefined,
    llmModels: mapLlmModels(data.llmModels),
  };
}
